import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Stack } from "@mui/material";

import { SAVE_BEFORE_ACTING } from "./shipmentDisplay";
import { formatFromExportUrl, LifecycleStepKey } from "./shipmentLifecycle";
import { StepAction } from "./StepAction";

/**
 * The saved-state moves available from the rail, each keyed to the step it
 * advances so the rail can render it under that step.
 *
 * Keyed to the status, not to edit mode: these act on what is already
 * persisted, so hiding them until someone clicks Edit would make reviewing a
 * requested shipment and shipping it two separate errands. Pending edits
 * disable them, with the reason, rather than removing them.
 *
 * The request document lives here rather than in the page's overflow menu. It
 * is produced by the Request pickup step and it is the evidence that step
 * happened, so opening it and re-sending it belong under the step that says
 * "Requested" — not in a menu of shipment-wide actions where they sat beside
 * deleting the shipment.
 */
export function shipmentStepActions({
  canEdit,
  isDraft,
  isRequested,
  hasUnsaved,
  requestExportUrl,
  requestDocumentExists,
  onRequestPickup,
  onMarkShipped,
  onViewRequest,
}: {
  canEdit: boolean;
  isDraft: boolean;
  isRequested: boolean;
  hasUnsaved: boolean;
  /**
   * A link to the last generated request, for opening it. Null both when none
   * was ever produced and when signing the link happened to fail, so it answers
   * "can this be opened right now" and not "does one exist".
   */
  requestExportUrl: string | null | undefined;
  /**
   * Whether a request document was ever produced — `requestGeneratedAt`, which
   * cannot fail the way a signed link can. Kept apart from the link so a signing
   * failure hides the View action without also changing what the page claims
   * happened, which is how the button and the stale-request alert came to be
   * able to contradict each other.
   */
  requestDocumentExists: boolean;
  onRequestPickup: () => void;
  onMarkShipped: () => void;
  onViewRequest: () => void;
}): Partial<Record<LifecycleStepKey, React.ReactNode>> {
  const blocked = hasUnsaved ? SAVE_BEFORE_ACTING : "";
  const actions: Partial<Record<LifecycleStepKey, React.ReactNode>> = {};

  const format = formatFromExportUrl(requestExportUrl);
  const requestActions = [
    // Opening the document is a read, so it outlives the shipment becoming
    // uneditable: a shipped shipment's request is still what the vendor was
    // sent, and still the thing someone will want to check it against.
    requestExportUrl ? (
      <StepAction
        key="view"
        label={format ? `View request (${format})` : "View request"}
        blocked=""
        endIcon={<OpenInNew />}
        onClick={onViewRequest}
      />
    ) : null,
    canEdit && isDraft ? (
      <StepAction
        key="request"
        label="Request pickup"
        blocked={blocked}
        onClick={onRequestPickup}
      />
    ) : null,
    // "Updated" only once the vendor holds a document to update. A request
    // arranged by phone leaves none, so for that shipment this offers to send
    // the first one — the action is the same dialog either way.
    canEdit && isRequested ? (
      <StepAction
        key="resend"
        label={requestDocumentExists ? "Send updated request" : "Send request"}
        blocked={blocked}
        onClick={onRequestPickup}
      />
    ) : null,
  ].filter(Boolean);

  if (requestActions.length > 0) {
    actions.request = (
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {requestActions}
      </Stack>
    );
  }

  if (canEdit) {
    actions.shipped = (
      <StepAction
        label="Mark shipped"
        icon={<LocalShippingOutlined />}
        blocked={blocked}
        onClick={onMarkShipped}
      />
    );
  }

  return actions;
}
